import { module } from 'modujs';
import { Loader } from "@googlemaps/js-api-loader";
import { BREAKPOINTS } from '../config';
import { $screenDebounce } from '../stores/screen';

export default class GMap extends module {

    // Static config
    static SETTINGS = {
        mapId: 'f59becc718d694cb',
        center: { lat: 38, lng: -95 },
        zoom: { desktop: 4, mobile: 3 }
    };

    constructor(m) {
        super(m);

        // UI elements
        this.$el = this.el;
        this.$container = this.$('container')[0];
        this.$accordions = Array.from(this.$('accordion'));
        this.$panel = this.$('panel')[0];
        this.$markers = [];

        // Data
        this.fetchUrl = this.getData('fetch-url') || null;
        this.apiKey = this.getData('api-key') || null;
        this.locations = [];
        this.activeLocation = this.createEmptyLocation();
        this.hasLocationActive = false;

        // Computed metrics
        this.offsetX = 0;
        this.zoom = this.calculateZoom();

        // Bindings
        this.handleResize = this.handleResize.bind(this);
        this.handleAccordionClick = this.handleAccordionClick.bind(this);
    }

    ///////////////
    // Lifecycle
    ///////////////
    init() {
        if (!this.apiKey) return;
        this.initMap();
        this.bindEvents();
        this.updateOffset();
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
        this.map = null;
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        this.unbindScreenListener = $screenDebounce.listen(this.handleResize);
        this.$accordions.forEach($el => $el.addEventListener('click', this.handleAccordionClick));
    }

    unbindEvents() {
        this.unbindScreenListener?.();
        this.$accordions.forEach($el => $el.removeEventListener('click', this.handleAccordionClick));
    }

    ///////////////
    // Map Initialization
    ///////////////
    async initMap() {
        const loader = new Loader({ apiKey: this.apiKey, version: "weekly" });

        try {
            await loader.load();
            const { Map } = await google.maps.importLibrary('maps');
            const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

            // Map options
            const options = {
                mapId: GMap.SETTINGS.mapId,
                disableDefaultUI: true,
                scrollwheel: false,
                zoom: this.zoom.initial,
                center: GMap.SETTINGS.center
            };

            // Initialize map
            this.map = new Map(this.$container, options);

            // Offset the map center after slight delay
            setTimeout(() => {
                this.map.setCenter(this.offsetCenter(GMap.SETTINGS.center, this.zoom.initial, this.offsetX));
            }, 5);

            // Fetch locations
            const data = await this.fetchLocations();
            this.locations = data;

            // Create markers
            for (const location of this.locations.items) {
                const [lat, lng] = location.coordinates.map(parseFloat);
                if (!lat || !lng) {
                    console.warn(`Invalid coordinates for location ${location.name}`);
                    continue;
                }

                // Create marker HTML element
                const markerEl = document.createElement('div');
                markerEl.dataset.locationId = location.id;
                markerEl.classList.add('c-map_marker');
                this.$markers.push(markerEl);

                // Create and add marker to map
                const marker = new AdvancedMarkerElement({
                    map: this.map,
                    position: { lat, lng },
                    content: markerEl
                });

                // Click handler for marker
                marker.addListener('click', () => {
                    const id = location.id;
                    const accordionEl = this.$accordions.find($el => $el.dataset.locationId === id);
                    const accordionModuleID = accordionEl?.dataset.moduleAccordion;

                    const selected = { id, latLng: { lat, lng }, $markerEl: markerEl, accordionModuleID };

                    if (this.activeLocation.id === id) {
                        this.deactivateLocation(selected, true);
                        this.call('shrink', null, 'Accordion', accordionModuleID);
                    } else {
                        this.activateLocation(selected);
                        this.call('open', null, 'Accordion', accordionModuleID);
                    }
                });
            }

        } catch (e) {
            console.error('Google Maps failed to load:', e);
        }
    }

    ///////////////
    // Data Fetching
    ///////////////
    async fetchLocations() {
        try {
            const response = await fetch(`${window.location.origin}${this.fetchUrl}`);
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            return await response.json();
        } catch (error) {
            console.error("Error fetching office locations:", error);
            return [];
        }
    }

    ///////////////
    // Callbacks
    ///////////////
    handleAccordionClick(e) {
        const $el = e.currentTarget;
        const id = $el.dataset.locationId;
        const accordionModuleID = $el.dataset.moduleAccordion;

        const location = this.locations.items.find(item => item.id === id);
        const $markerEl = this.$markers.find(el => el.dataset.locationId === id);

        if (!location || !$markerEl) return;

        const latLng = {
            lat: parseFloat(location.coordinates[0]),
            lng: parseFloat(location.coordinates[1])
        };

        const selected = { id, latLng, $markerEl, accordionModuleID };

        if (this.activeLocation.id === id) {
            this.deactivateLocation(selected, true);
        } else {
            this.activateLocation(selected);
        }
    }

    handleResize({ width }) {
        // Recalculate offset and zoom levels
        this.updateOffset();
        this.zoom = this.calculateZoom(width);

        const zoom = this.hasLocationActive ? this.zoom.active : this.zoom.initial;
        const center = this.hasLocationActive ? this.activeLocation.latLng : GMap.SETTINGS.center;

        if (this.map.getZoom() !== zoom) this.map.setZoom(zoom);
        this.map.panTo(this.offsetCenter(center, zoom, this.offsetX));
    }

    ///////////////
    // Map Logic
    ///////////////
    activateLocation(location) {
        const { id, latLng, $markerEl, accordionModuleID } = location;

        if (this.hasLocationActive) {
            this.deactivateLocation(this.activeLocation);
            this.call('shrink', null, 'Accordion', this.activeLocation.accordionModuleID);
        }

        $markerEl?.classList.add('is-active');
        if (this.map.getZoom() === this.zoom.initial) this.map.setZoom(this.zoom.active);
        this.map.panTo(this.offsetCenter(latLng, this.zoom.active, this.offsetX));

        this.activeLocation = location;
        this.hasLocationActive = true;
    }

    deactivateLocation(location, reset = false) {
        const { $markerEl } = location;
        $markerEl?.classList.remove('is-active');

        if (reset) {
            this.map.setZoom(this.zoom.initial);
            this.map.panTo(this.offsetCenter(GMap.SETTINGS.center, this.zoom.initial, this.offsetX));
            this.activeLocation = this.createEmptyLocation();
            this.hasLocationActive = false;
        }
    }

    ///////////////
    // Utils
    ///////////////
    offsetCenter(latLng, zoom, offsetX = 0, offsetY = 0) {
        // Calculate an offset center point based on zoom level and pixel offset
        const scale = Math.pow(2, zoom);
        const worldPoint = this.map.getProjection().fromLatLngToPoint(latLng);
        const pixelOffset = new google.maps.Point(offsetX / scale, offsetY / scale);

        const newPoint = new google.maps.Point(
            worldPoint.x - pixelOffset.x,
            worldPoint.y + pixelOffset.y
        );

        return this.map.getProjection().fromPointToLatLng(newPoint);
    }

    updateOffset() {
        // Adjust X offset based on panel width and screen size
        this.offsetX = window.innerWidth > BREAKPOINTS.FROM_SM ? (this.$panel.offsetWidth / 2 || 0) : 0;
    }

    calculateZoom(width = window.innerWidth) {
        const base = width > BREAKPOINTS.FROM_SM ? GMap.SETTINGS.zoom.desktop : GMap.SETTINGS.zoom.mobile;
        return { initial: base, active: base + 1 };
    }

    createEmptyLocation() {
        // Default structure for an inactive location
        return { id: null, latLng: {}, $markerEl: null, accordionModuleID: null };
    }
}
